import { Cesium } from 'app/cesium';
import { CesiumAsset } from 'app/map/geometry/cesium/CesiumAsset';
import { OffsetManager } from 'app/map/geometry/cesium/OffsetManager';
import { CesiumMap } from 'app/map/map';
import { TerrainService } from 'app/map/services/terrain.service';
import {
    AssetLocationProviderFn,
    createLocationProviderFn,
} from 'app/shared/asset_location_provider';
import { Asset } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { LocationStatCollection } from 'app/shared/collections/location_stat_collection.service';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { View } from 'app/shared/collections/view';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import DebugService from 'app/shared/debug.service';
import { ImageUrlService } from 'app/shared/image_url.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { selectionsToSetOfAssetIDs } from 'app/shared/selection/selection_utilities';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { SiteStateService } from 'app/shared/site_state.service';
import { throttleCollectionChanges } from 'app/shared/throttled_collection_change_listener';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { Toast } from 'app/shared/toast/toast.service';
import { slamCartesianFurtherIntoGround } from 'app/shared/utilities/mapping_utils';
import { ViewStateService } from 'app/shared/view_state.service';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { IMSVisualiser } from 'app/views/visualiser';
import { partition } from 'lodash';
import { AssetTypeCollection } from 'app/shared/collections/asset_type_collection.service';
import { ConfigService } from 'app/config/config.service';
import { getTimelineAppropriateUnix } from 'app/map/geometry/cesium/CesiumAgricultureUtils';
import { entityTypeFromString } from 'app/agriculture/models/Entities';

const log = (...args) => DebugService.log('CesiumAssetManager', ...args);

export class CesiumAssetManager {
    // Dependencies
    private readonly _assetCollection: AssetCollection;
    private readonly _clockHandler: ClockHandler;
    private readonly _viewStateService: ViewStateService;
    private readonly _locationStatCollection: LocationStatCollection;
    private readonly _offsetManager: OffsetManager;
    private readonly _imageUrlService: ImageUrlService;
    private readonly _cesiumMap: CesiumMap;
    private readonly _terrainService: TerrainService;
    private readonly _imsItemTypeRegistry: ImsItemTypeRegistry;
    private readonly _assetOptionsService: MapOptionsService;
    private readonly _configService: ConfigService;

    // Geometry state
    private _assets: CesiumAsset[];
    private _assetIconCollection: Cesium.BillboardCollection;
    private _selectionCollection: Cesium.BillboardCollection;
    private _labelCollection: Cesium.LabelCollection;
    private _polylineCollection: Cesium.PolylineCollection;
    private _extraLabelCollection: Cesium.LabelCollection;
    private _primitiveCollection: Cesium.PrimitiveCollection;

    // Replicated IMS state
    private _hiddenAssets: Set<number>;
    private _selectedAssets: Set<number | string>;
    private _currentImsVisualiser: IMSVisualiser;
    private _removeIndicatorListener: () => void;
    private _requestIndicatorUpdateOnNextFrame: boolean;
    private _getAssetLocationFn: AssetLocationProviderFn;
    private _assetTypeNameFilterSet: Set<string> | null;
    private _assetTypeCollection: AssetTypeCollection;

    constructor(
        cesiumMap: CesiumMap,
        clockHandler: ClockHandler,
        // collections
        assetCollection: AssetCollection,
        zoneCollection: ZoneCollection,
        locationStatCollection: LocationStatCollection,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        imageUrlService: ImageUrlService,
        terrainService: TerrainService,
        // on-change services
        selectionService: SelectionService,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        assetOptionsService: MapOptionsService,
        offsetManager: OffsetManager,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        assetTypeCollection: AssetTypeCollection,
        configService: ConfigService,
    ) {
        this._cesiumMap = cesiumMap;
        this._assetCollection = assetCollection; // TODO REMOVE: Exists because of hacked requirement from other services (requires imsasset, vs ID)
        this._clockHandler = clockHandler; // To get the current time for requesting current position/selection information
        this._viewStateService = viewStateService; // To get the appropriate visualization data, given time.
        this._locationStatCollection = locationStatCollection; // For position information, given time.
        this._imageUrlService = imageUrlService;
        this._offsetManager = offsetManager;
        this._terrainService = terrainService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._assetOptionsService = assetOptionsService;
        this._assetTypeCollection = assetTypeCollection;
        this._configService = configService;

        this._assets = [];

        this._hiddenAssets = new Set();
        this._selectedAssets = new Set();

        this._assetIconCollection = new Cesium.BillboardCollection({
            scene: cesiumMap.viewer.scene,
        }); // asset icon
        this._selectionCollection = new Cesium.BillboardCollection(); // selection
        this._labelCollection = new Cesium.LabelCollection({ scene: cesiumMap.viewer.scene }); // label
        this._polylineCollection = new Cesium.PolylineCollection(); // height pole
        this._extraLabelCollection = new Cesium.LabelCollection(); // extra label
        this._primitiveCollection = new Cesium.PrimitiveCollection();

        // make sure we have a location provider function set in the constructor
        this._updateLocationProvider();
        this._updateAssetTypeNameFilterSet();

        this._cesiumMap.viewer.scene.primitives.add(this._assetIconCollection);
        if (!this._configService.enableOpacityChangeOnAssetSelection) {
            this._cesiumMap.viewer.scene.primitives.add(this._selectionCollection);
        }
        this._cesiumMap.viewer.scene.primitives.add(this._labelCollection);
        this._cesiumMap.viewer.scene.primitives.add(this._polylineCollection);
        this._cesiumMap.viewer.scene.primitives.add(this._extraLabelCollection);
        this._cesiumMap.viewer.scene.primitives.add(this._primitiveCollection);

        // On site change clear all cesium data.
        siteStateService.onSiteSelectionChanged(this._onSiteChange.bind(this));

        viewStateService.onViewSelectionChanged(() => {
            this._updateIndicatorChangeListener();
            this._updateLocationProvider();
            this._updateAssetTypeNameFilterSet();

            // update every asset now we've probably changed some internal properties
            this._processAssetData();
        });

        terrainService.subscribeChangeOfTerrain(() => {
            this._updateLocationProvider();
            this.updateLocations();
        });

        // Don't worry about cleaning this up - it's a service so it never gets destroyed
        // When any collection even remotely sneezes - obliterate the entities and make sure they're up to date.
        //  It's a nuclear approach to the fact that you can color/label an asset by a stat with a foreign key to
        //  another asset, an ip device or a zone - and we don't have a good way of knowing if anything changes and
        //  who cares about that change.
        throttleCollectionChanges(100, [zoneCollection, assetCollection], () =>
            this._processAssetData(),
        );

        this._cesiumMap.viewer.scene.preUpdate.addEventListener(() => {
            // update all the indicators for each cesium asset if an update has been requested,
            //  and do it right before the cesium frame update/render (pre-update)
            // this stops the indicators from being recalculated multiple times per frame
            //  which is useless. Kind of like a throttle - but a once-per-frame-and-just-before-render-throttle
            if (this._requestIndicatorUpdateOnNextFrame) {
                // reset this so for the next frame
                this._requestIndicatorUpdateOnNextFrame = false;
                const indicatorManager =
                    this._viewStateService.currentViewController.indicatorManager;
                let i = this._assets.length;
                while (i !== 0) {
                    --i;
                    const cesiumAsset = this._assets[i];
                    if (cesiumAsset) {
                        cesiumAsset.setExtraComponents(
                            indicatorManager.getCurrentIndicators(cesiumAsset.id),
                        );
                    }
                }
            }
        });

        // Initialise current IMSVisualiser
        this._currentImsVisualiser =
            this._viewStateService.currentViewController.getAssetVisualiserForCurrentMapOptions();

        // For show/hide from quicklist
        this._manageHiddenAssets(imsObjectVisibilityService.getHiddenAssetIds());
        imsObjectVisibilityService.subscribeToAssetChanges(() => {
            log('Updating from visibility service');
            this._manageHiddenAssets(imsObjectVisibilityService.getHiddenAssetIds());
        });

        // For selection information
        this._manageSelectedAssets(selectionsToSetOfAssetIDs(selectionService.getSelection()));
        selectionService.onSelectionChange(() => {
            log('Updating from selection service');
            this._manageSelectedAssets(selectionsToSetOfAssetIDs(selectionService.getSelection()));
            this._updateAlphaOnSelection();
        });

        assetOptionsService.addOnMapOptionsChanged(() => {
            // Update ImsVisualiser, we get it using 'getAssetVisualiserForCurrentMapOptions' to ensure it's accurate
            // as it's possible that this listener will fire *before* the listener in the currentViewController
            this._currentImsVisualiser =
                this._viewStateService.currentViewController.getAssetVisualiserForCurrentMapOptions();
            // Can improve this by checking what type of value changed and updating path appropriately.
            log('Updating from assetOptionsService');
            this._updateLocationProvider();
            this._recalculateAllShowValues();
            this.updateData();
        });

        locationStatCollection.addOnChangedListener(() => {
            log('Updating CesiumAssets from location stats');
            this._recalculateAllShowValues();
        });

        // Handle stat collections loading data
        imsItemTypeRegistry.addOnImsItemTypeRegistryReady(() => {
            const statNames = imsItemTypeRegistry.getRegisteredStatTypes();
            const collections: Array<ImsTimeCollection> = statNames.map(
                (statName) => imsItemTypeRegistry.getTypeDetails(statName).collection,
            );
            collections.forEach((collection) => {
                collection.addOnChangedListener(this.updateData.bind(this));
                collection.addOnIntervalsChangedListener(this.updateData.bind(this));
            });
        });

        this._clockHandler.addOnTickListener(this.updateLocations.bind(this));
        this._clockHandler.addOnCursorMovedListener(() => {
            this.updateData();
        }, this._configService.imsBaseTimeResolution);
    }

    _updateIndicatorChangeListener() {
        // Destroy existing
        this._removeIndicatorListener ? this._removeIndicatorListener() : null;
        // Create new
        const indicatorManager = this._viewStateService.currentViewController.indicatorManager;
        this._removeIndicatorListener = indicatorManager.onChange(() => {
            this._requestIndicatorUpdateOnNextFrame = true;
        });
        this._requestIndicatorUpdateOnNextFrame = true;
    }

    public flyToAssetById(id: number): void {}

    _onSiteChange() {
        log('Removing all assets');
        this.deleteAll();
    }

    _processAssetData() {
        const [assets, filteredOutAssets] = getAssetsForMap(
            this._assetCollection,
            this._assetTypeNameFilterSet,
        );
        log(`Processing ${assets.length} asset for cesium updates`);
        this._offsetManager.pause();
        for (const asset of assets) {
            if (asset.isDeleted) {
                this.delete(asset);
            } else {
                this.update(asset);
            }
        }

        // make sure we have deleted any assets we don't want to see
        for (const asset of filteredOutAssets) {
            this.delete(asset);
        }

        this._offsetManager.unpause();
        // After we've finished updating assets force offsets to be updated.
        this._offsetManager.updateAll();

        // stuff has probably changed, make sure indicators are all up to date as well
        this._requestIndicatorUpdateOnNextFrame = true;
    }

    _recalculateAllShowValues() {
        const theTime = this._clockHandler.getCursorTimeAsJulian();

        let i = this._assets.length;
        while (i !== 0) {
            --i;
            const cesiumAsset = this._assets[i];
            const imsAsset = this._assetCollection.get(cesiumAsset.id);
            if (!imsAsset) {
                continue;
            }
            this._calculateShow(cesiumAsset);
        }

        // stuff has probably changed, make sure indicators are all up to date as well
        this._requestIndicatorUpdateOnNextFrame = true;
    }

    _calculateShow(cesiumAsset: CesiumAsset) {
        cesiumAsset.hide();
    }

    _updateCesiumAssetFromAsset(imsAsset: Asset, cesiumAsset: CesiumAsset) {
        cesiumAsset.image = this._currentImsVisualiser.entityVisualiser.getAssetImage(imsAsset);

        const theTime = this._clockHandler.getCursorTimeAsJulian();
        this._calculateShow(cesiumAsset);
        this.updateDataForAsset(cesiumAsset);

        this._requestIndicatorUpdateOnNextFrame = true;
    }

    _manageHiddenAssets(hiddenIDs: Set<number>) {
        if (!hiddenIDs) {
            // Should this set an empty set?
            return;
        }
        this._hiddenAssets = hiddenIDs;
        this._recalculateAllShowValues();
    }

    _manageSelectedAssets(selectedIDs: Set<number | string>) {
        if (!selectedIDs) {
            // Should this set an empty set?
            return;
        }
        this._selectedAssets = selectedIDs;
        this._assets.forEach((cesiumAsset) => {
            if (selectedIDs.has(cesiumAsset.id)) {
                return cesiumAsset.select();
            }
            cesiumAsset.deselect();
        });
        this._offsetManager.updateAll();
    }

    _updateAlphaOnSelection() {
        if (!this._configService.enableOpacityChangeOnAssetSelection) {
            return;
        }

        this._assets.forEach((asset) => {
            // if there is at least 1 selected asset, but this asset isn't selected
            if (this._selectedAssets.size > 0 && !this._selectedAssets.has(asset.id)) {
                asset.alpha = 0.4;
            } else {
                asset.alpha = 1;
            }
        });

        this.updateData();
    }

    get(id: number): CesiumAsset {
        return this._assets.find((cesiumAsset) => {
            return cesiumAsset.id === id;
        });
    }

    // Used by KMZ Service currently, avoid using where possible.
    getAll(): CesiumAsset[] {
        return this._assets;
    }

    private getAssetPositionAndShow(id: number): {
        position: Cesium.Cartesian3;
        isShowingIcon: boolean;
    } {
        const asset = this.get(id);
        return {
            position: asset?.position,
            isShowingIcon: asset?.isShowingIcon(),
        };
    }

    create(imsAsset: Asset) {
        if (this.get(imsAsset.id)) {
            // Asset exists, update instead.
            return this.update(imsAsset);
        }
        // Create it
        const cesiumAsset = new CesiumAsset(
            imsAsset.id,
            imsAsset.typeId,
            this._assetIconCollection,
            this._selectionCollection,
            this._polylineCollection,
            this._labelCollection,
            this._extraLabelCollection,
            this._primitiveCollection,
            this._imageUrlService,
            this._offsetManager,
            this.getAssetPositionAndShow.bind(this),
            this._assetTypeCollection,
        );

        // Add it
        this._assets.push(cesiumAsset);

        // Update from visible/selected
        if (this._selectedAssets && this._selectedAssets.has(cesiumAsset.id)) {
            cesiumAsset.select();
        } else {
            cesiumAsset.deselect();
        }
        if (this._hiddenAssets && this._hiddenAssets.has(cesiumAsset.id)) {
            cesiumAsset.hide();
        } else {
            cesiumAsset.show();
        }

        // Make required updates before making it a cesium thing
        this._updateCesiumAssetFromAsset(imsAsset, cesiumAsset);
    }

    update(imsAsset: Asset) {
        const cesiumAsset = this.get(imsAsset.id);
        if (cesiumAsset === undefined) {
            return this.create(imsAsset);
        }
        this._updateCesiumAssetFromAsset(imsAsset, cesiumAsset);
    }

    _delete(cesiumAsset: CesiumAsset) {
        if (cesiumAsset === undefined) {
            return;
        }

        // Delete it.
        cesiumAsset.destroy();

        // Remove from our list.
        this._assets = this._assets.filter((x) => {
            return !(cesiumAsset.id === x.id);
        });
    }

    delete(imsAsset: Asset) {
        this._delete(this.get(imsAsset.id));
    }

    deleteAll() {
        // This should probably be managed somewhere else
        // TODO when we add more offsets to this, relocate this
        this._offsetManager.deleteAll();

        this._assetIconCollection.removeAll();
        this._selectionCollection.removeAll();
        this._labelCollection.removeAll();
        this._polylineCollection.removeAll();
        this._extraLabelCollection.removeAll();
        this._primitiveCollection.removeAll();

        this._assets = [];
    }

    private updateLocations(): void {
        let id: number, cesiumAsset: CesiumAsset, isVisible: boolean;

        let i = this._assets.length;
        while (i !== 0) {
            --i;
            cesiumAsset = this._assets[i];
            id = cesiumAsset.id;
            const asset = this._assetCollection.get(id);
            const entityType = entityTypeFromString(asset?.type?.name);
            const unixTime = getTimelineAppropriateUnix(entityType, this._clockHandler);

            // Don't bother spending resources updating if currently not visible
            isVisible = cesiumAsset.isVisible();
            if (!isVisible) {
                continue;
            }
            const groundPosition = this._getAssetLocationFn(id, unixTime);
            if (cesiumAsset)
                if (this._terrainService.mapTerrainEnabled) {
                    const elevatedPosition = this._locationStatCollection.getPositionAtUnixTime(
                        id,
                        unixTime,
                        true,
                    );
                    cesiumAsset.setPositions(
                        elevatedPosition,
                        slamCartesianFurtherIntoGround(groundPosition),
                    );
                } else {
                    cesiumAsset.setPositions(groundPosition, null);
                }
        }

        // todo this is only here for connection line view components
        //  since they track locations of 2 assets (near and far)
        //  again this is screaming for a the cesium asset manager
        //  to draw the connection lines, not each individual cesium asset object
        //  because then the manager would know which asset locations changed
        //  and which connection lines are going to/from that asset
        if (this._configService.updateAssetViewComponentsWithLocation) {
            this._requestIndicatorUpdateOnNextFrame = true;
        }
    }

    updateDataForAsset(cesiumAsset: CesiumAsset) {
        cesiumAsset.hide();
    }

    updateData() {
        let cesiumAsset: CesiumAsset;

        const time = this._clockHandler.getCursorTimeAsJulian();

        this._offsetManager.pause();

        let i = this._assets.length;
        while (i !== 0) {
            --i;
            cesiumAsset = this._assets[i];
            this.updateDataForAsset(cesiumAsset);
        }
        this._offsetManager.unpause();
    }

    private _updateLocationProvider() {
        this._getAssetLocationFn = createLocationProviderFn(
            this._locationStatCollection,
            this._imsItemTypeRegistry,
            this._assetCollection,
            this._viewStateService.currentViewController,
            this._assetOptionsService,
        );
    }

    private _updateAssetTypeNameFilterSet() {
        this._assetTypeNameFilterSet = getAssetTypeNameFilterSetFromView(
            this._viewStateService.currentViewController.getView(),
        );
    }
}

/**
 * Returns a tuple of assets to show on the map, and assets to not show on the map
 */
function getAssetsForMap(
    assetCollection: AssetCollection,
    assetTypeNameFilterSet: Set<string>,
): [allowedAssets: Array<Asset>, deniedAssets: Array<Asset>] {
    // const allAssets = assetCollection.getAllAsArray();
    const allAssets: Asset[] = [];
    // if the filter set is null, that means ignore the filter altogether
    if (!assetTypeNameFilterSet) {
        return [allAssets, []];
    }

    return partition(allAssets, (asset) =>
        assetTypeNameFilterSet.has(asset.type?.name.toLowerCase()),
    );
}

function getAssetTypeNameFilterSetFromView(view: View | null): Set<string> | null {
    const configs = view?.assetTypeNameMapAllowLists || [];
    const names = configs.length
        ? configs
              .map((config) => config.asset_type_names || [])
              .flat()
              .map((name) => name.toLowerCase())
        : null;

    // if we get a non-null, then use it, otherwise keep the null
    // this means you can hide everything from the map if you want, but putting an empty list in a config)
    return names ? new Set(names) : null;
}
